<template>
    <div>
        <Panel
            :title="$t('Access Points')"
            :add-button="
                $store.hasRight('EDIT_ACCESS_TYPE_4') || $store.hasRight('EDIT_ACCESS_TYPE_8')
                    ? $t('New Access Point')
                    : ''
            "
            :delete-button="selected.length > 0"
            :page="page"
            :total-rows="totalRows"
            @input="openForm"
            @change="changePage"
            @delete="handleDelete"
        >
            <table id="accesspoints_table" class="cic-table">
                <thead>
                    <tr>
                        <th v-if="newEmail && items.some((x) => x.mailboxes.length > 0)"></th>
                        <th>{{ $t('Access Point') }}</th>
                        <th>{{ $t('Type') }}</th>
                        <th class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="point in items" @click="(evt) => handleOpen(point.accessId, evt)">
                        <td v-if="newEmail && items.some((x) => x.mailboxes.length > 0)">
                            <template v-if="point.mailboxes.length">
                                <span
                                    v-if="point.mailboxes.some((x) => x.failed)"
                                    class="inactive"
                                    :title="point.mailboxes.find((x) => x.failed).errorMessage"
                                >
                                </span>
                                <span v-else class="active"></span>
                            </template>
                        </td>
                        <td class="primary">
                            {{ point.name }}
                        </td>
                        <td>{{ $t('ACCESS_TYPE_' + point.type) }}</td>
                        <td>
                            <cic-checkbox
                                v-if="$store.hasRight('EDIT_ACCESS_TYPE_' + point.type)"
                                :value="selected.indexOf(point.accessId) !== -1"
                                @click="handleSelected(point.accessId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <template #footer>
                <cic-button v-if="newEmail" id="azure_button" @click="azureRedirect">
                    {{ $t('Grant Access - AzureAD') }}
                </cic-button>
            </template>
        </Panel>

        <edit-access-point
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            :new-email="
                newEmail && (!item.accessId || item.mailboxes.length === 0 || item.mailboxes[0].failed !== undefined)
            "
            :surveys="surveys"
            @blur="closePopup"
            @input="saveItem"
            @change="updateFieldValue"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import Panel from '@/components/container/Panel';
    import CicButton from '@/components/CicButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { baseUrl, deleteItems, load, save } from '@/utils/Http';
    import EditAccessPoint from './EditAccessPoint';
    import './styles/accesspoints.scss';

    export default {
        name: 'AccessPoints',
        components: {
            CicButton,
            CicCheckbox,
            EditAccessPoint,
            Panel,
        },
        mixins: [TableUtils],
        data() {
            return {
                item: null,
                items: [],
                newEmail: false,
                page: 1,
                selected: [],
                show: true,
                surveys: [],
                totalRows: 0,
            };
        },
        created() {
            this.loadTable();
        },
        methods: {
            azureRedirect() {
                load('email/granturl', {}, (response) => {
                    window.location.assign(
                        response.url.replace('<REDIRECT_URI>', `${baseUrl()}/inboundnumbers/accesspoints`),
                    );
                });
            },

            handleDelete() {
                deleteItems('accesspoints/delete', { accessIds: this.selected }, this.loadTable);
            },

            loadTable() {
                this.selected = [];
                this.closePopup();
                load('accesspoints/editable', { page: this.page }, (response) => {
                    this.items = response.accessPoints;
                    this.newEmail = response.newEmail;
                    this.totalRows = response.total;
                    this.surveys = response.surveys;
                });
            },

            openForm(accessId) {
                if (accessId > 0) {
                    load('accesspoint/editable', { accessId }, (response) => {
                        this.item = response.accessPoint;
                    });
                } else {
                    this.item = {
                        accessId: null,
                        name: '',
                        type: null,
                        accountId: null,
                        mailboxes: null,
                        surveyId: null,
                    };
                }
            },

            saveEmail() {
                if (this.item.type === 4 && this.newEmail && this.item.mailboxes.length) {
                    let imap = this.item.mailboxes[0].imapFlags;
                    if (imap.trim() !== '') {
                        try {
                            JSON.parse(imap);
                        } catch (e) {
                            alert(this.$t('IMAP Flags must be JSON string'));
                            return;
                        }
                    }
                }

                const params = {
                    accessId: this.item.accessId,
                    name: this.item.name,
                    addressType: this.item.accountId,
                    mailboxes: JSON.stringify(this.item.mailboxes),
                };
                save('accesspoint/updateemail', params, (response) => {
                    if (response.urls && confirm(this.$t('Authorise [x]').replace(/\[x]/g, response.urls[0].address))) {
                        window.localStorage.setItem('email_auth', JSON.stringify(response.urls));
                        window.location.assign(response.urls[0].url);
                    } else {
                        this.loadTable();
                    }
                });
            },

            saveIm() {
                const params = {
                    accessId: this.item.accessId,
                    name: this.item.name,
                    surveyId: this.item.surveyId,
                };
                save('accesspoint/updateim', params, (response) => {
                    if (!this.item.accessId) {
                        this.$store.showSuccessMessage(
                            this.$t('Update successful. Mailbox URL is [x].').replace(/\[x]/g, response.url),
                        );
                    }

                    this.loadTable();
                });
            },

            saveItem() {
                if (this.item.type === 4) {
                    this.saveEmail();
                } else {
                    this.saveIm();
                }
            },

            updateFieldValue(field, value) {
                let item = { ...this.item };
                item[field] = value;
                if (field === 'type') {
                    item.accountId = value === 4 ? 0 : null;
                    item.mailboxes = null;
                    if (value === 4) {
                        item.mailboxes = [
                            {
                                address: '',
                                server: '',
                                port: 0,
                                imapFlags: this.newEmail ? '' : '/ssl',
                                retries: 3,
                                mainMailbox: this.$t('INBOX'),
                                archive: false,
                                failed: null,
                                authType: 0,
                                reauthorise: false,
                            },
                        ];
                    }
                }

                this.item = item;
            },
        },
    };
</script>
